import { lazy, type ComponentType } from 'react'

const RETRY_KEY = 'chunk-retry'

function getRetryFlag(): boolean {
  try {
    return sessionStorage.getItem(RETRY_KEY) === '1'
  } catch {
    // sessionStorage unavailable (private browsing, storage full, etc.)
    return false
  }
}

function setRetryFlag(): void {
  try {
    sessionStorage.setItem(RETRY_KEY, '1')
  } catch {
    // Best-effort — if storage fails, reload still helps, just no loop guard
  }
}

function removeRetryFlag(): void {
  try {
    sessionStorage.removeItem(RETRY_KEY)
  } catch {
    // Ignore
  }
}

/**
 * Wraps React.lazy with a single-reload retry for stale chunk errors.
 * When a deployment replaces hashed assets, browsers with cached HTML
 * try to fetch old chunk filenames that no longer exist (404).
 * This catches that and reloads the page once to get fresh HTML.
 * On second failure, it throws normally (caught by ErrorBoundary).
 */
// Every route registers its import factory here so the app can warm route
// chunks during idle time (see prefetchRegisteredRoutes). Without prefetch,
// navigating to a subpage pays a cold network fetch for its (often large)
// code-split chunk — the cause of "top-level pages fast, subpages slow".
const routeFactories: Array<() => Promise<unknown>> = []

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  routeFactories.push(factory)
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (!getRetryFlag()) {
        setRetryFlag()
        window.location.reload()
        // Wait for the reload — if it doesn't happen within 5s (e.g. blocked
        // by beforeunload handler or browser extension), fall through to
        // ErrorBoundary instead of hanging in Suspense forever.
        return new Promise<{ default: T }>((_resolve, reject) => {
          setTimeout(() => reject(error), 5000)
        })
      }
      // Already retried once — clear flag and let ErrorBoundary handle it
      removeRetryFlag()
      throw error
    })
  )
}

/**
 * Call on successful app mount to clear the retry flag.
 * This ensures the next stale-chunk error gets a fresh retry.
 */
export function clearChunkRetry() {
  removeRetryFlag()
}

let prefetchStarted = false

function scheduleIdle(cb: () => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback
  if (typeof ric === 'function') {
    ric(cb, { timeout: 3000 })
  } else {
    setTimeout(cb, 300)
  }
}

/**
 * Warm every registered route chunk during browser idle time so navigating to
 * a subpage hits the cache instead of paying a cold network fetch. Runs once,
 * respects Save-Data / slow connections, and never throws — a prefetch failure
 * is harmless because the real navigation re-fetches via lazyWithRetry.
 *
 * Chunks are fetched a few at a time in route-registration order (common
 * top-level routes first), yielding to idle between each so prefetch never
 * competes with the data the current page is loading.
 */
export function prefetchRegisteredRoutes(): void {
  if (prefetchStarted) return
  prefetchStarted = true

  if (typeof navigator !== 'undefined') {
    const conn = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection
    if (conn?.saveData) return
    if (typeof conn?.effectiveType === 'string' && conn.effectiveType.includes('2g')) return
  }

  let index = 0
  const CONCURRENCY = 2
  const pump = (): void => {
    if (index >= routeFactories.length) return
    const factory = routeFactories[index]
    index += 1
    Promise.resolve()
      .then(factory)
      .catch(() => undefined)
      .finally(() => scheduleIdle(pump))
  }
  for (let lane = 0; lane < CONCURRENCY; lane += 1) {
    scheduleIdle(pump)
  }
}

/** Test-only: reset prefetch state so the once-guard can be re-exercised. */
export function resetPrefetchForTest(): void {
  prefetchStarted = false
}
